/**
 * 美編 prompt 模板（REQ-20260429001）。
 * 純字串拼接,給使用者複製到 ChatGPT 跑 GPT Image 2 用。
 * 文案要改直接改這個檔案,不需要動後端。
 */

export type DesignTemplateGroup = 'ad' | 'social' | 'customer';

export interface DesignTemplate {
  id: string;
  group: DesignTemplateGroup;
  label: string;
  build: (note: string) => string;
}

export const groupLabels: Record<DesignTemplateGroup, string> = {
  ad: '📢 廣告與電商',
  social: '📱 社群貼文',
  customer: '👤 客戶溝通',
};

function section(title: string, lines: string[]): string {
  return `【${title}】\n${lines.map((l) => `- ${l}`).join('\n')}`;
}

function trailer(size: string, note: string): string {
  const noteBlock = note.trim() ? note.trim() : '無';
  return `【尺寸建議】\n${size}\n\n【補充需求】\n${noteBlock}`;
}

export const DESIGN_TEMPLATES: DesignTemplate[] = [
  // ─── 📢 廣告與電商 ──────────────────────────────────────────────
  {
    id: 'ecom_white_bg',
    group: 'ad',
    label: '電商商品白底圖',
    build: (note) =>
      `請將參考圖製作為電商商品形象圖,用於商品上架展示。

${section('風格要求', [
  '純白色背景,畫面乾淨無雜物與多餘元素',
  '商品置中,構圖平衡,留白充足',
  '柔和均勻打光,避免強烈反光與陰影',
  '顏色真實還原,材質紋理清晰可辨',
  '整體質感專業、高級',
])}

${trailer('1024 x 1024(1:1 正方形)', note)}`,
  },
  {
    id: 'brand_image',
    group: 'ad',
    label: '品牌形象廣告',
    build: (note) =>
      `請將參考圖製作為品牌形象廣告主視覺,用於官網 banner / 廣告投放。

${section('風格要求', [
  '電影感、情境化的構圖,情緒鮮明',
  '主視覺偏一側,另一側留白給標題與品牌 logo',
  '色調統一、有故事感',
  '光線層次豐富,具高級攝影感',
  '畫面整體質感符合中高端品牌調性',
])}

${trailer('1920 x 1080(16:9 橫幅)', note)}`,
  },
  {
    id: 'promo_poster',
    group: 'ad',
    label: '折扣促銷海報',
    build: (note) =>
      `請將參考圖製作為折扣促銷海報主視覺,用於電商首頁 / 社群推播。

${section('風格要求', [
  '醒目對比色配色,視覺衝擊力強',
  '商品鮮明居中,周圍預留空間放折扣標籤、主標題',
  '可加入活力動感元素(光點、線條、節慶元素)',
  '整體吸睛,讓人在 1 秒內理解「這在打折」',
])}

${trailer('1080 x 1350(3:4 直幅,IG / 電商通用)', note)}`,
  },
  {
    id: 'food_menu',
    group: 'ad',
    label: '餐飲菜單照',
    build: (note) =>
      `請將參考圖製作為餐廳菜單 / 外送平台用的料理形象照。

${section('風格要求', [
  '自然光感、餐桌情境氛圍',
  '暖色調為主,讓食物看起來美味誘人',
  '俯拍或 45 度視角,構圖飽滿',
  '突顯食材的色澤、紋理與份量感',
  '可帶入餐具、桌布、配菜等情境元素,但不喧賓奪主',
])}

${trailer('1080 x 1440(3:4 直幅)', note)}`,
  },

  // ─── 📱 社群貼文 ────────────────────────────────────────────────
  {
    id: 'ig_square',
    group: 'social',
    label: 'IG 方圖貼文',
    build: (note) =>
      `請將參考圖製作為 Instagram 方形貼文圖。

${section('風格要求', [
  '社群感強、視覺乾淨,符合 IG 美學',
  '主體置中或黃金比例構圖',
  '色調統一(可考慮輕度濾鏡感)',
  '預留上下或單邊空間以利疊放標題文字',
  '避免過度鋭利的商業氣息,要有「想點讚」的氛圍',
])}

${trailer('1080 x 1080(1:1 方形)', note)}`,
  },
  {
    id: 'ig_story',
    group: 'social',
    label: 'IG 限時動態 / Reels 封面',
    build: (note) =>
      `請將參考圖製作為 Instagram Stories / Reels 直幅封面。

${section('風格要求', [
  '直幅構圖,主視覺位於畫面中央偏上',
  '上方預留約 15% 給用戶名與時間戳記、下方預留約 20% 給互動按鈕',
  '色彩飽和、第一眼吸睛',
  '可包含動態感的視覺元素(模糊背景、漸層光暈)',
])}

${trailer('1080 x 1920(9:16 直幅)', note)}`,
  },
  {
    id: 'fb_post',
    group: 'social',
    label: 'Facebook 貼文',
    build: (note) =>
      `請將參考圖製作為 Facebook 動態貼文用主視覺。

${section('風格要求', [
  '橫幅構圖,中央放主視覺,左右各預留留白',
  '畫面比 IG 更正式、資訊感稍重',
  '色調沉穩、可信任感',
  '若需放置標題文字,請在右側留出乾淨區塊',
])}

${trailer('1200 x 628(1.91:1 橫幅)', note)}`,
  },
  {
    id: 'line_oa',
    group: 'social',
    label: 'LINE 圖文訊息',
    build: (note) =>
      `請將參考圖製作為 LINE 官方帳號圖文訊息主視覺。

${section('風格要求', [
  '正方形構圖,主視覺清晰',
  '品牌色彩明確,可疊加標題文字而不雜亂',
  '預留下半部 1/3 區塊給活動文字 / CTA',
  '視覺風格適合台灣消費者(親切、實用、不過度設計)',
])}

${trailer('1040 x 1040(1:1 方形,LINE 標準尺寸)', note)}`,
  },

  // ─── 👤 客戶溝通 ────────────────────────────────────────────────
  {
    id: 'customer_confirm',
    group: 'customer',
    label: '客戶確認稿',
    build: (note) =>
      `請將參考圖製作為「給客戶確認」的提案稿,需附上規格說明文字。

${section('風格要求', [
  '主視覺乾淨,商品/設計重點清楚可辨',
  '畫面下方或側邊預留資訊區,放規格、尺寸、注意事項',
  '可使用簡單線條/箭頭標示重點部位',
  '整體偏簡報風格,讓客戶能一眼讀懂',
])}

${trailer('1024 x 1024(1:1 方形,易插入 LINE / Email)', note)}`,
  },
  {
    id: 'custom',
    group: 'customer',
    label: '自訂(完全自由)',
    build: (note) => {
      const trimmed = note.trim();
      if (!trimmed) {
        return '(請在「補充說明」中描述你想要的處理方向,系統會原樣使用你的描述。)';
      }
      return trimmed;
    },
  },
];
